import { ReactNode } from 'react';

import { FlexAligner, IconButton, Tooltip, TooltipProps, Typography } from '@scaleaq/scaleui';
import { icon } from '@scaleaq/sui-tokens/colors';
import { spacing } from '@scaleaq/sui-tokens/responsive';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { colors, Icon, responsive } from '@/utils/scaleUi';

type FieldArrayItemProps = {
  title: string;
  onRemove?: () => void;
  onReset?: () => void;
  disableReset?: boolean;
  disableRemove?: boolean;
  resetButtonTooltip?: string;
  removeButtonTooltip?: string;
  children: ReactNode;
  tooltipProps?: Omit<TooltipProps, 'children' | 'tooltip'>;
  render?: boolean;
};
export const FieldArrayItem = ({
  title,
  onRemove,
  onReset,
  disableReset,
  disableRemove,
  resetButtonTooltip,
  removeButtonTooltip,
  children,
  tooltipProps,
  render = true,
}: FieldArrayItemProps) => {
  const { t } = useTranslation();
  const resetTooltip = resetButtonTooltip ?? t('reset');
  const removeTooltip = removeButtonTooltip ?? t('remove');
  if (!render) return;
  return (
    <FieldArrayItemWrapper direction='column' gap={spacing.large}>
      <FlexAligner alignment='center' gap={spacing.large} fullWidth={false}>
        <Typography type={'heading_2xsmall'} style={{ height: 'fitContent' }}>
          {title}
        </Typography>
        {(onReset || onRemove) && (
          <FlexAligner
            alignment='center'
            gap={spacing.large}
            fullWidth={false}
            style={{ marginLeft: 'auto' }}
          >
            {onReset && (
              <Tooltip {...tooltipProps} tooltip={resetTooltip}>
                <StyledIconButton
                  size='large'
                  Icon={<Icon.Undo size='large' />}
                  variant={'soft'}
                  disabled={disableReset}
                  onClick={() => onReset()}
                />
              </Tooltip>
            )}
            {onRemove && (
              <Tooltip {...tooltipProps} tooltip={removeTooltip}>
                <StyledIconButton
                  size='large'
                  Icon={Icon.X}
                  variant={'soft'}
                  onClick={() => onRemove()}
                  disabled={disableRemove}
                />
              </Tooltip>
            )}
          </FlexAligner>
        )}
      </FlexAligner>
      {children}
    </FieldArrayItemWrapper>
  );
};
// ************************************
// Styled components
// ************************************
const FieldArrayItemWrapper = styled(FlexAligner)`
  padding: ${spacing.large};
  border: 0.0625rem solid ${colors.stroke.neutral['100']};
  border-radius: ${responsive.radius.xlarge};
`;
const StyledIconButton = styled(IconButton)`
  ${({ disabled }) =>
    disabled &&
    css`
      svg {
        color: ${icon.disabled};
      }
    `}
`;
